<form [formGroup]="form">
  <div class="vitals-print-section" [class.wrap]="wrapContent">
    <div class="vitals-print-header print-header">
      @if (!payload?.properties?.hideLabel?.value && payload?.properties?.text?.value) {
        <div class="header">
          <div class="title">
            <span [innerHTML]="payload?.properties?.text?.value | tokenToDiv"></span>
            @if (payload?.properties?.required?.value) {
              <span class="required-asterisk">*</span>
            }
          </div>
          <span class="message">* populated from PatientNow</span>
        </div>
      }

      @if (payload?.properties?.content?.value) {
        <div class="flex flex-row">
          <div class="flex word-wrap mb10">
            <div class="flex subtitle">
              <span [innerHTML]="payload?.properties?.content?.value | tokenToDiv"></span>
            </div>
          </div>
        </div>
      }

      <mat-divider></mat-divider>
    </div>

    <ng-template
      #fieldLabel
      let-control="control"
      let-controls="controls"
      let-forControl="forControl"
      let-label="label"
    >
      <label class="vitals-label" [attr.for]="controlId(forControl || control)">
        <span class="vitals-label__content">
          <span>{{ label }}</span>
          <fb-scribe-suggestion-indicator
            [target]="vitalsMarkerTarget"
            [fieldKey]="control"
            [fieldKeys]="controls"
          ></fb-scribe-suggestion-indicator>
          <fb-amendment-field-indicator [fieldKey]="control" [fieldKeys]="controls" />
        </span>
      </label>
    </ng-template>

    @if (isFormLoaded$ | async) {
      <div class="vitals-container">
        <!-- Row: Height + Weight -->
        <div class="vitals-row">
          <div class="vitals-field vitals-field--half">
            <div class="vitals-control">
              <ng-container
                *ngTemplateOutlet="
                  fieldLabel;
                  context: { controls: ['height_ft', 'height_in'], forControl: 'height_ft', label: 'Height' }
                "
              ></ng-container>
              <div class="vitals-input-group">
                <div class="vitals-control">
                  <ng-container
                    *ngTemplateOutlet="
                      decimalField;
                      context: {
                        control: 'height_ft',
                        id: controlId('height_ft'),
                        ph: 'ft',
                        format: '1.0-10',
                        normalizeEnabled: true,
                      }
                    "
                  ></ng-container>
                  <ng-container
                    *ngTemplateOutlet="errorField; context: { control: 'height_ft', title: 'Height ft' }"
                  ></ng-container>
                </div>
                <div class="vitals-control">
                  <ng-container
                    *ngTemplateOutlet="
                      decimalField;
                      context: {
                        control: 'height_in',
                        id: controlId('height_in'),
                        ph: 'in',
                        format: '1.0-10',
                        normalizeEnabled: true,
                      }
                    "
                  ></ng-container>
                  <ng-container
                    *ngTemplateOutlet="errorField; context: { control: 'height_in', title: 'Height in' }"
                  ></ng-container>
                </div>
              </div>
            </div>
          </div>

          <div class="vitals-field vitals-field--half">
            <div class="vitals-control">
              <ng-container
                *ngTemplateOutlet="fieldLabel; context: { control: 'weight', label: 'Weight' }"
              ></ng-container>
              <ng-container
                *ngTemplateOutlet="
                  decimalField;
                  context: {
                    control: 'weight',
                    id: controlId('weight'),
                    ph: 'lbs',
                    format: '1.2-2',
                  }
                "
              ></ng-container>
              <ng-container
                *ngTemplateOutlet="errorField; context: { control: 'weight', title: 'Weight' }"
              ></ng-container>
            </div>
          </div>
        </div>

        <!-- Row: Goal Weight + Body Fat -->
        <div class="vitals-row">
          <div class="vitals-field vitals-field--half">
            <div class="vitals-control">
              <ng-container
                *ngTemplateOutlet="fieldLabel; context: { control: 'goal_weight', label: 'Goal Weight' }"
              ></ng-container>
              <ng-container
                *ngTemplateOutlet="
                  decimalField;
                  context: {
                    control: 'goal_weight',
                    id: controlId('goal_weight'),
                    ph: 'lbs',
                    format: '1.2-2',
                  }
                "
              ></ng-container>
              <ng-container
                *ngTemplateOutlet="errorField; context: { control: 'goal_weight', title: 'Goal Weight' }"
              ></ng-container>
            </div>
          </div>

          <div class="vitals-field vitals-field--half">
            <div class="vitals-control">
              <ng-container
                *ngTemplateOutlet="fieldLabel; context: { control: 'body_fat_percentage', label: 'Body Fat %' }"
              ></ng-container>
              <ng-container
                *ngTemplateOutlet="
                  decimalField;
                  context: {
                    control: 'body_fat_percentage',
                    id: controlId('body_fat_percentage'),
                    ph: '',
                    format: '1.2-2',
                  }
                "
              ></ng-container>
              <ng-container
                *ngTemplateOutlet="errorField; context: { control: 'body_fat_percentage', title: 'Body Fat %' }"
              ></ng-container>
            </div>
          </div>
        </div>

        <!-- Row: StartDiff + PrevWeight -->
        <div class="vitals-row">
          <div class="vitals-field vitals-field--half">
            <div class="vitals-control">
              <ng-container
                *ngTemplateOutlet="fieldLabel; context: { control: 'start_diff', label: '+/- Start' }"
              ></ng-container>
              <ng-container
                *ngTemplateOutlet="
                  textField;
                  context: { control: 'start_diff', id: controlId('start_diff'), ph: 'lbs' }
                "
              ></ng-container>
              @if (showAutoCalcHint$ | async) {
                <div class="vitals-calculation-hint">
                  <span>{{ autoCalculationHint }}</span>
                </div>
              }
            </div>
          </div>
          <div class="vitals-field vitals-field--half">
            <div class="vitals-control">
              <ng-container
                *ngTemplateOutlet="fieldLabel; context: { control: 'prev_weight', label: 'Prev Weight' }"
              ></ng-container>
              <ng-container
                *ngTemplateOutlet="
                  textField;
                  context: { control: 'prev_weight', id: controlId('prev_weight'), ph: 'lbs' }
                "
              ></ng-container>
              @if (showAutoCalcHint$ | async) {
                <div class="vitals-calculation-hint">
                  <span>{{ autoCalculationHint }}</span>
                </div>
              }
            </div>
          </div>
        </div>

        <!-- Row: LastVisitDiff + BP -->
        <div class="vitals-row">
          <div class="vitals-field vitals-field--half">
            <div class="vitals-control">
              <ng-container
                *ngTemplateOutlet="fieldLabel; context: { control: 'last_visit_diff', label: '+/- Last Visit' }"
              ></ng-container>
              <ng-container
                *ngTemplateOutlet="
                  textField;
                  context: {
                    control: 'last_visit_diff',
                    id: controlId('last_visit_diff'),
                    ph: 'lbs',
                  }
                "
              ></ng-container>
              @if (showAutoCalcHint$ | async) {
                <div class="vitals-calculation-hint">
                  <span>{{ autoCalculationHint }}</span>
                </div>
              }
            </div>
          </div>
          <div class="vitals-field vitals-field--half">
            <div class="vitals-control">
              <ng-container
                *ngTemplateOutlet="fieldLabel; context: { control: 'blood_pressure', label: 'B/P' }"
              ></ng-container>
              <ng-container
                *ngTemplateOutlet="textField; context: { control: 'blood_pressure', id: controlId('blood_pressure') }"
              ></ng-container>
              <ng-container
                *ngTemplateOutlet="errorField; context: { control: 'blood_pressure', title: 'B/P' }"
              ></ng-container>
            </div>
          </div>
        </div>

        <!-- Row: BMI + Pulse -->
        <div class="vitals-row">
          <div class="vitals-field vitals-field--half">
            <div class="vitals-control">
              <ng-container *ngTemplateOutlet="fieldLabel; context: { control: 'bmi', label: 'BMI' }"></ng-container>
              <ng-container
                *ngTemplateOutlet="decimalField; context: { control: 'bmi', id: controlId('bmi'), format: '1.1-1' }"
              ></ng-container>
              @if (showAutoCalcHint$ | async) {
                <div class="vitals-calculation-hint">
                  <span>{{ autoCalculationHint }}</span>
                </div>
              }
            </div>
          </div>
          <div class="vitals-field vitals-field--half">
            <div class="vitals-control">
              <ng-container
                *ngTemplateOutlet="fieldLabel; context: { control: 'pulse', label: 'Pulse' }"
              ></ng-container>
              <ng-container
                *ngTemplateOutlet="
                  decimalField;
                  context: {
                    control: 'pulse',
                    id: controlId('pulse'),
                    format: '1.0-10',
                    normalizeEnabled: true,
                  }
                "
              ></ng-container>
              <ng-container
                *ngTemplateOutlet="errorField; context: { control: 'pulse', title: 'Pulse' }"
              ></ng-container>
            </div>
          </div>
        </div>

        <!-- Row: BMR + Respiration -->
        <div class="vitals-row">
          <div class="vitals-field vitals-field--half">
            <div class="vitals-control">
              <ng-container *ngTemplateOutlet="fieldLabel; context: { control: 'bmr', label: 'BMR' }"></ng-container>
              <ng-container
                *ngTemplateOutlet="
                  decimalField;
                  context: {
                    control: 'bmr',
                    id: controlId('bmr'),
                    format: '1.0-10',
                    normalizeEnabled: true,
                  }
                "
              ></ng-container>
              <ng-container *ngTemplateOutlet="errorField; context: { control: 'bmr', title: 'BMR' }"></ng-container>
            </div>
          </div>
          <div class="vitals-field vitals-field--half">
            <div class="vitals-control">
              <ng-container
                *ngTemplateOutlet="fieldLabel; context: { control: 'respiration', label: 'Respir.' }"
              ></ng-container>
              <ng-container
                *ngTemplateOutlet="
                  decimalField;
                  context: {
                    control: 'respiration',
                    id: controlId('respiration'),
                    format: '1.0-10',
                    normalizeEnabled: true,
                  }
                "
              ></ng-container>
              <ng-container
                *ngTemplateOutlet="errorField; context: { control: 'respiration', title: 'Respir.' }"
              ></ng-container>
            </div>
          </div>
        </div>

        <!-- Row: TDEE + Last Menstrual -->
        <div class="vitals-row">
          <div class="vitals-field vitals-field--half">
            <div class="vitals-control">
              <ng-container *ngTemplateOutlet="fieldLabel; context: { control: 'tdee', label: 'TDEE' }"></ng-container>
              <ng-container
                *ngTemplateOutlet="
                  decimalField;
                  context: {
                    control: 'tdee',
                    id: controlId('tdee'),
                    format: '1.0-10',
                    normalizeEnabled: true,
                  }
                "
              ></ng-container>
              <ng-container *ngTemplateOutlet="errorField; context: { control: 'tdee', title: 'TDEE' }"></ng-container>
            </div>
          </div>
          <div class="vitals-field vitals-field--half">
            <div class="vitals-control">
              <ng-container
                *ngTemplateOutlet="fieldLabel; context: { control: 'last_menstrual', label: 'Last Menstrual' }"
              ></ng-container>
              <ng-container
                *ngTemplateOutlet="dateField; context: { control: 'last_menstrual', id: controlId('last_menstrual') }"
              ></ng-container>
              <ng-container *ngTemplateOutlet="errorField; context: { control: 'last_menstrual' }"></ng-container>
            </div>
          </div>
        </div>

        <!-- Row: Fitzpatrick -->
        <div class="vitals-row">
          <div class="vitals-field vitals-field--half">
            <div class="vitals-control">
              <ng-container
                *ngTemplateOutlet="fieldLabel; context: { control: 'fitzpatrick_scale', label: 'Fitzpatrick Scale' }"
              ></ng-container>
              <ng-container
                *ngTemplateOutlet="
                  selectField;
                  context: { control: 'fitzpatrick_scale', id: controlId('fitzpatrick_scale') }
                "
              ></ng-container>
              <ng-container
                *ngTemplateOutlet="errorField; context: { control: 'fitzpatrick_scale', title: 'Fitzpatrick Scale' }"
              ></ng-container>
            </div>
          </div>
          <div class="vitals-field vitals-field--half"></div>
        </div>

        <!-- Row: Notes -->
        <div class="vitals-row">
          <div class="vitals-field vitals-field--full">
            <div class="vitals-control">
              <ng-container
                *ngTemplateOutlet="fieldLabel; context: { control: 'vital_notes', label: 'Vital Notes' }"
              ></ng-container>
              <ng-container
                *ngTemplateOutlet="
                  textareaField;
                  context: { control: 'vital_notes', id: controlId('vital_notes'), rows: 1 }
                "
              ></ng-container>
              <ng-container
                *ngTemplateOutlet="errorField; context: { control: 'vital_notes', title: 'Vital Notes' }"
              ></ng-container>
            </div>
          </div>
        </div>

        <!-- Row: Waist + Hip -->
        @if (isFieldVisible('waist') || isFieldVisible('hip')) {
          <div class="vitals-row">
            @if (isFieldVisible('waist')) {
              <div class="vitals-field vitals-field--half">
                <div class="vitals-control">
                  <ng-container
                    *ngTemplateOutlet="fieldLabel; context: { control: 'waist', label: 'Waist' }"
                  ></ng-container>
                  <ng-container
                    *ngTemplateOutlet="
                      decimalField;
                      context: {
                        control: 'waist',
                        id: controlId('waist'),
                        ph: 'in',
                        format: '1.1-1',
                      }
                    "
                  ></ng-container>
                  <ng-container
                    *ngTemplateOutlet="errorField; context: { control: 'waist', title: 'Waist' }"
                  ></ng-container>
                </div>
              </div>
            }

            @if (isFieldVisible('hip')) {
              <div class="vitals-field vitals-field--half">
                <div class="vitals-control">
                  <ng-container
                    *ngTemplateOutlet="fieldLabel; context: { control: 'hip', label: 'Hip' }"
                  ></ng-container>
                  <ng-container
                    *ngTemplateOutlet="
                      decimalField;
                      context: { control: 'hip', id: controlId('hip'), ph: 'in', format: '1.1-1' }
                    "
                  ></ng-container>
                  <ng-container
                    *ngTemplateOutlet="errorField; context: { control: 'hip', title: 'Hip' }"
                  ></ng-container>
                </div>
              </div>
            }
          </div>
        }

        <!-- Row: Chest/Bust -->
        @if (isFieldVisible('chest_bust')) {
          <div class="vitals-row">
            <div class="vitals-field vitals-field--half">
              <div class="vitals-control">
                <ng-container
                  *ngTemplateOutlet="fieldLabel; context: { control: 'chest_bust', label: 'Chest/Bust' }"
                ></ng-container>
                <ng-container
                  *ngTemplateOutlet="
                    decimalField;
                    context: {
                      control: 'chest_bust',
                      id: controlId('chest_bust'),
                      ph: 'in',
                      format: '1.1-1',
                    }
                  "
                ></ng-container>
                <ng-container
                  *ngTemplateOutlet="errorField; context: { control: 'chest_bust', title: 'Chest/Bust' }"
                ></ng-container>
              </div>
            </div>

            <div class="vitals-field vitals-field--half"></div>
          </div>
        }

        <!-- Row: Left Arm + Right Arm -->
        @if (isFieldVisible('left_arm') || isFieldVisible('right_arm')) {
          <div class="vitals-row">
            @if (isFieldVisible('left_arm')) {
              <div class="vitals-field vitals-field--half">
                <div class="vitals-control">
                  <ng-container
                    *ngTemplateOutlet="fieldLabel; context: { control: 'left_arm', label: 'Left Arm' }"
                  ></ng-container>
                  <ng-container
                    *ngTemplateOutlet="
                      decimalField;
                      context: {
                        control: 'left_arm',
                        id: controlId('left_arm'),
                        ph: 'in',
                        format: '1.1-1',
                      }
                    "
                  ></ng-container>
                  <ng-container
                    *ngTemplateOutlet="errorField; context: { control: 'left_arm', title: 'Left Arm' }"
                  ></ng-container>
                </div>
              </div>
            }

            @if (isFieldVisible('right_arm')) {
              <div class="vitals-field vitals-field--half">
                <div class="vitals-control">
                  <ng-container
                    *ngTemplateOutlet="fieldLabel; context: { control: 'right_arm', label: 'Right Arm' }"
                  ></ng-container>
                  <ng-container
                    *ngTemplateOutlet="
                      decimalField;
                      context: {
                        control: 'right_arm',
                        id: controlId('right_arm'),
                        ph: 'in',
                        format: '1.1-1',
                      }
                    "
                  ></ng-container>
                  <ng-container
                    *ngTemplateOutlet="errorField; context: { control: 'right_arm', title: 'Right Arm' }"
                  ></ng-container>
                </div>
              </div>
            }
          </div>
        }

        <!-- Row: Left Thigh + Right Thigh -->
        @if (isFieldVisible('left_thigh') || isFieldVisible('right_thigh')) {
          <div class="vitals-row">
            @if (isFieldVisible('left_thigh')) {
              <div class="vitals-field vitals-field--half">
                <div class="vitals-control">
                  <ng-container
                    *ngTemplateOutlet="fieldLabel; context: { control: 'left_thigh', label: 'Left Thigh' }"
                  ></ng-container>
                  <ng-container
                    *ngTemplateOutlet="
                      decimalField;
                      context: {
                        control: 'left_thigh',
                        id: controlId('left_thigh'),
                        ph: 'in',
                        format: '1.1-1',
                      }
                    "
                  ></ng-container>
                  <ng-container
                    *ngTemplateOutlet="errorField; context: { control: 'left_thigh', title: 'Left Thigh' }"
                  ></ng-container>
                </div>
              </div>
            }

            @if (isFieldVisible('right_thigh')) {
              <div class="vitals-field vitals-field--half">
                <div class="vitals-control">
                  <ng-container
                    *ngTemplateOutlet="fieldLabel; context: { control: 'right_thigh', label: 'Right Thigh' }"
                  ></ng-container>
                  <ng-container
                    *ngTemplateOutlet="
                      decimalField;
                      context: {
                        control: 'right_thigh',
                        id: controlId('right_thigh'),
                        ph: 'in',
                        format: '1.1-1',
                      }
                    "
                  ></ng-container>
                  <ng-container
                    *ngTemplateOutlet="errorField; context: { control: 'right_thigh', title: 'Right Thigh' }"
                  ></ng-container>
                </div>
              </div>
            }
          </div>
        }

        <!-- Dynamic: neck + vtc -->
        @if (isFieldVisible('neck') || isFieldVisible('vtc')) {
          <div class="vitals-row">
            @if (isFieldVisible('neck')) {
              <div class="vitals-field vitals-field--half">
                <div class="vitals-control">
                  <ng-container
                    *ngTemplateOutlet="fieldLabel; context: { control: 'neck', label: 'Neck' }"
                  ></ng-container>
                  <ng-container
                    *ngTemplateOutlet="
                      decimalField;
                      context: {
                        control: 'neck',
                        id: controlId('neck'),
                        ph: 'in',
                        format: '1.1-1',
                      }
                    "
                  ></ng-container>
                  <ng-container
                    *ngTemplateOutlet="errorField; context: { control: 'neck', title: 'Neck' }"
                  ></ng-container>
                </div>
              </div>
            }

            @if (isFieldVisible('vtc')) {
              <div class="vitals-field vitals-field--half">
                <div class="vitals-control">
                  <ng-container
                    *ngTemplateOutlet="fieldLabel; context: { control: 'vtc', label: 'VTC' }"
                  ></ng-container>
                  <ng-container
                    *ngTemplateOutlet="
                      decimalField;
                      context: { control: 'vtc', id: controlId('vtc'), ph: 'in', format: '1.1-1' }
                    "
                  ></ng-container>
                  <ng-container
                    *ngTemplateOutlet="errorField; context: { control: 'vtc', title: 'VTC' }"
                  ></ng-container>
                </div>
              </div>
            }
          </div>
        }
        @if (isEmrReadonly) {
          <fb-emr-readonly-notice />
        }
      </div>
    } @else {
      <div class="vitals-spinner"><mat-spinner class="loading-spinner" diameter="40"></mat-spinner></div>
    }

    @if (payload?.properties?.hint?.value) {
      <div class="hint" [innerHTML]="payload?.properties?.hint?.value | tokenToDiv"></div>
    }

    @if (payload?.properties?.required?.value && isSubmitted && form.invalid) {
      <div class="hint-required">This field is required</div>
    }
  </div>

  <ng-template
    #decimalField
    let-control="control"
    let-id="id"
    let-ph="ph"
    let-format="format"
    let-aria="aria"
    let-normalizeEnabled="normalizeEnabled"
  >
    @let isInvalid = isSubmitted && form.controls?.[control]?.invalid;
    <input
      decimalDisplay
      normalizeNumeric
      class="vitals-input"
      type="text"
      [ngClass]="{ 'is-invalid': isInvalid }"
      [decimalFormat]="format"
      [formControlName]="control"
      [placeholder]="ph || ''"
      [id]="id"
      [attr.aria-label]="aria"
      [normalizeEnabled]="!!normalizeEnabled"
      (input)="clearVitalsMarker(control)"
    />
  </ng-template>

  <ng-template #textField let-control="control" let-id="id" let-ph="ph" let-aria="aria">
    @let isInvalid = isSubmitted && form.controls?.[control]?.invalid;
    <input
      class="vitals-input"
      type="text"
      [ngClass]="{ 'is-invalid': isInvalid }"
      [formControlName]="control"
      [placeholder]="ph || ''"
      [id]="id"
      [attr.aria-label]="aria"
      (input)="clearVitalsMarker(control)"
    />
  </ng-template>

  <ng-template #dateField let-control="control" let-id="id">
    @let isInvalid = isSubmitted && form.controls?.[control]?.invalid;
    <div class="date-input-wrapper">
      <input
        dateInputGuard
        class="vitals-input"
        type="text"
        placeholder="MM/DD/YYYY"
        [ngClass]="{ 'is-invalid': isInvalid }"
        [formControlName]="control"
        [id]="id"
        [matDatepicker]="picker"
        (focus)="!form.disabled && picker.open()"
        (input)="clearVitalsMarker(control)"
        (dateChange)="clearVitalsMarker(control)"
      />
      <button
        class="date-picker-btn"
        aria-label="Open calendar"
        type="button"
        [disabled]="form.disabled"
        (click)="!form.disabled && picker.open()"
      >
        <rx-icon name="icon-calendar2"></rx-icon>
      </button>
      <mat-datepicker #picker></mat-datepicker>
    </div>
  </ng-template>

  <ng-template #selectField let-control="control" let-id="id">
    @let isInvalid = isSubmitted && form.controls?.[control]?.invalid;
    <ng-select
      class="vitals-select"
      dropdownPosition="auto"
      appendTo="body"
      bindLabel="text"
      bindValue="value"
      placeholder="Select"
      [ngClass]="{ 'is-invalid': isInvalid }"
      [formControlName]="control"
      [id]="id"
      [items]="fitzpatrickOptions"
      [searchable]="false"
      [clearable]="false"
      (change)="clearVitalsMarker(control)"
    >
      <ng-template ng-option-tmp let-item="item">{{ item.text }}</ng-template>
    </ng-select>
  </ng-template>

  <ng-template #textareaField let-control="control" let-id="id" let-rows="rows">
    <textarea
      appPasteStrip
      appTextareaAutosize
      [appTextareaAutosizeUnlimited]="isPrinting"
      class="vitals-textarea"
      [class.vitals-textarea--print]="isPrinting"
      [formControlName]="control"
      [id]="id"
      (input)="clearVitalsMarker(control)"
    ></textarea>
  </ng-template>

  <ng-template #errorField let-controlName="control" let-title="title">
    @let control = form?.controls?.[controlName];
    @let errors = control?.errors;

    @if (control?.invalid && isSubmitted) {
      <div class="error-message">
        @if (errors?.min) {
          <span>{{ title }} {{ errorTexts.min }} {{ errors.min.min }}.</span>
        } @else if (errors?.max) {
          <span>{{ title }} {{ errorTexts.max }} {{ errors.max.max }}.</span>
        } @else if (errors?.matDatepickerParse) {
          <span>{{ errorTexts.invalidDateFormat }}</span>
        }
      </div>
    }
  </ng-template>
</form>
